import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { getAccountIsRBL } from '@helpers/Accounts/getAccountIsRBL';
import { getInLoanDrawdownOffer } from '@helpers/Accounts/getInLoanDrawdownOffer';
import { getTopUpStatus } from '@helpers/GetTopUpStatus/getTopUpStatus';
import { TTopUpStatus } from '@helpers/GetTopUpStatus/getTopUpStatus.types';
import {
    fetchGetApplication,
    GetAccountsResponse
} from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';

import { useStateKey } from '../../../../iwQueryClient/useStateKey';

type TAccount = GetAccountsResponse['data']['accounts'][0];
type TFormalOffer =
    GetAccountsResponse['data']['accounts'][0]['formal_offers'][0];

type TUseGetDrawdownActionPanelData = {
    drawdownOffer: TFormalOffer | undefined;
    topupInterestRate: number | undefined;
    creditLimit: TFormalOffer['max_amount'];
    topUpStatus: TTopUpStatus;
    availableCash: TFormalOffer['available_cash'] | 0;
    isInInterestOnlyRepaymentPeriod: TAccount['is_in_interest_only_repayment_period'];
    isRBL: boolean;
};

export const useGetDrawdownActionPanelData = (
    accountId: NonNullable<TAccount['account_id']>
): TUseGetDrawdownActionPanelData => {
    const stateKey = useStateKey();

    const { data: account } = useQuery({
        ...getAccountsQueryConfig(stateKey),
        select: (data) =>
            data.data.accounts.find(
                (account) => account.account_id === accountId
            ),
        suspense: true
    });

    const { data: application } = useQuery({
        queryKey: ['application', stateKey],
        queryFn: () =>
            fetchGetApplication({
                stateKey
            }),
        select: (data) => data.data,
        suspense: true
    });

    if (!account || !application) {
        throw new Error();
    }

    return {
        drawdownOffer: getInLoanDrawdownOffer(account),
        topupInterestRate: getLatestFormalOfferMonthlyInterestRate(account),
        creditLimit: getDrawdownCreditLimit(account),
        topUpStatus: getTopUpStatus(account, application),
        availableCash: getAvailableCash(account),
        isInInterestOnlyRepaymentPeriod:
            account.is_in_interest_only_repayment_period,
        isRBL: getAccountIsRBL(account)
    };
};

const getLatestFormalOfferMonthlyInterestRate = (
    account: TAccount
): number | undefined => {
    const drawdownOffer = getInLoanDrawdownOffer(account);
    return drawdownOffer?.interest_rates?.[0].rate;
};

const getDrawdownCreditLimit = (account: TAccount): number | undefined => {
    if (account.formal_offer_access_period) {
        return account.formal_offer_access_period!.available_credit;
    }
    const drawdownOffer = getInLoanDrawdownOffer(account);
    return drawdownOffer?.max_amount;
};

const getAvailableCash = (account: TAccount) => {
    if (account.formal_offer_access_period) {
        return account.formal_offer_access_period!.available_cash;
    } else return getInLoanDrawdownOffer(account)?.available_cash;
};
